import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { BusinessRegistrationForm } from '@/types/businessRegistration';
import { motion } from 'framer-motion';
import { 
  Edit2, CheckCircle, MapPin, Phone, Calendar, User, Briefcase, 
  Mail, Globe, Smartphone, Building2, Star, Shield, FileText,
  ArrowLeft, Eye, Badge, MapIcon, ExternalLink, MessageCircle
} from 'lucide-react';
import { Badge as UIBadge } from '@/components/ui/badge';

interface ReviewStepProps {
  formData: BusinessRegistrationForm;
  onBack: () => void;
  onSubmit: () => void;
  onEdit?: (step: 'personal' | 'business' | 'contact' | 'location' | 'locationConfirmation' | 'experience') => void;
}

export function ReviewStep({
  formData,
  onBack,
  onSubmit,
  onEdit
}: ReviewStepProps) {
  const renderEditButton = (step: 'personal' | 'business' | 'contact' | 'location' | 'locationConfirmation' | 'experience', ariaLabel: string) => {
    if (!onEdit) return null;
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onEdit(step)}
        className="absolute top-4 right-4 text-gray-500 hover:text-primary hover:bg-primary/10 transition-all duration-200"
        aria-label={ariaLabel}
      >
        <Edit2 className="h-4 w-4 mr-1" />
        Edit
      </Button>
    );
  };

  const formatBusinessType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatAddress = () => {
    const { address } = formData.businessData.location;
    return [
      address.street,
      address.city,
      address.state,
      address.country,
      address.postalCode
    ].filter(Boolean).join(', ');
  };

  const getBookingModeDisplay = () => {
    switch (formData.businessData.contact.bookingMode) {
      case 'platform':
        return 'Through Wildy Platform';
      case 'redirect':
        return 'Redirect to External Website';
      default:
        return formData.businessData.contact.bookingMode;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header Section */}
      <div className="text-center space-y-3 pb-6 border-b border-gray-200">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-[#97BC62] rounded-full flex items-center justify-center">
          <Eye className="h-8 w-8 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Review Your Information</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Please review all information below carefully. Click the edit button on any section to make changes.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Personal Information */}
        <Card className="p-6 relative border-l-4 border-l-blue-500">
          {renderEditButton('personal', 'Edit personal information')}
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <User className="h-5 w-5 text-blue-600" />
              <h4 className="font-semibold text-gray-900">Personal Information</h4>
              <UIBadge variant="secondary" className="ml-2">Account Owner</UIBadge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  Full Name
                </p>
                <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{formData.userData.name}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  Email Address
                </p>
                <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md font-mono text-sm">{formData.userData.email}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  Nationality
                </p>
                <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{formData.userData.nationality}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Date of Birth
                </p>
                <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {formData.userData.dateOfBirth instanceof Date
                    ? formData.userData.dateOfBirth.toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : new Date(formData.userData.dateOfBirth).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                </p>
              </div>
            </div>
          </motion.div>
        </Card>

        {/* Business Information */}
        <Card className="p-6 relative border-l-4 border-l-green-500">
          {renderEditButton('business', 'Edit business information')}
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="h-5 w-5 text-green-600" />
              <h4 className="font-semibold text-gray-900">Business Information</h4>
              <UIBadge variant="outline" className="ml-2">Main Profile</UIBadge>
            </div>
            
            {/* Business Name & Icon */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border-2 border-green-200">
                  <span className="text-2xl">{formData.businessData.emoji}</span>
                </div>
                <div>
                  <h5 className="font-semibold text-lg text-gray-900">{formData.businessData.name}</h5>
                  <p className="text-sm text-gray-600">{formatBusinessType(formData.businessData.type)}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  Business Description
                </p>
                <div className="bg-gray-50 px-3 py-2 rounded-md">
                  <p className="text-gray-900 text-sm leading-relaxed">{formData.businessData.description}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Activity Types Offered</p>
                <div className="flex gap-2">
                  {formData.businessData.hasAnimalActivities && (
                    <UIBadge className="bg-orange-100 text-orange-800 border-orange-200">
                      🦁 Animal Activities
                    </UIBadge>
                  )}
                  {formData.businessData.hasAdventureActivities && (
                    <UIBadge className="bg-blue-100 text-blue-800 border-blue-200">
                      🏔️ Adventure Activities
                    </UIBadge>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </Card>

        {/* Contact & Booking Information */}
        <Card className="p-6 relative border-l-4 border-l-purple-500">
          {renderEditButton('contact', 'Edit contact and booking information')}
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Phone className="h-5 w-5 text-purple-600" />
              <h4 className="font-semibold text-gray-900">Contact & Booking Information</h4>
              <UIBadge variant="outline" className="ml-2">Customer Facing</UIBadge>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  Business Email
                </p>
                <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md font-mono text-sm">{formData.businessData.contact.email}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Smartphone className="h-3.5 w-3.5" />
                  Business Phone
                </p>
                <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md font-mono text-sm">
                  {formData.businessData.contact.countryCode} {formData.businessData.contact.phone}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  Website
                </p>
                <div className="bg-gray-50 px-3 py-2 rounded-md">
                  {formData.businessData.contact.website ? (
                    <a 
                      href={formData.businessData.contact.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                    >
                      {formData.businessData.contact.website}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-gray-500 text-sm">Not provided</span>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  Booking Method
                </p>
                <div className="bg-gray-50 px-3 py-2 rounded-md">
                  <span className="text-gray-900 text-sm">{getBookingModeDisplay()}</span>
                  {formData.businessData.contact.bookingMode === 'platform' && (
                    <UIBadge className="ml-2 bg-green-100 text-green-800 text-xs">Recommended</UIBadge>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Contact Options */}
            {(formData.businessData.contact.bookingUrl || formData.businessData.contact.whatsappNumber) && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-900 mb-3">Additional Contact Options</h5>
                <div className="space-y-2">
                  {formData.businessData.contact.bookingUrl && (
                    <div className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">Booking URL: </span>
                      <a 
                        href={formData.businessData.contact.bookingUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm underline"
                      >
                        {formData.businessData.contact.bookingUrl}
                      </a>
                    </div>
                  )}
                  {formData.businessData.contact.whatsappNumber && (
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">WhatsApp: </span>
                      <span className="text-blue-900 font-mono text-sm">{formData.businessData.contact.whatsappNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </Card>

        {/* Location Information */}
        <Card className="p-6 relative border-l-4 border-l-red-500">
          {renderEditButton('location', 'Edit location information')}
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-red-600" />
              <h4 className="font-semibold text-gray-900">Business Location</h4>
              <UIBadge variant="outline" className="ml-2">Verified</UIBadge>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <MapIcon className="h-3.5 w-3.5" />
                  Business Address
                </p>
                <div className="bg-gray-50 px-3 py-2 rounded-md">
                  <p className="text-gray-900 text-sm leading-relaxed">{formatAddress()}</p>
                </div>
              </div>
              {formData.businessData.location.coordinates && 
               formData.businessData.location.coordinates.coordinates && 
               formData.businessData.location.coordinates.coordinates.length === 2 && (
                <div className="text-xs text-gray-500 bg-green-50 px-3 py-2 rounded-md border border-green-200">
                  <Shield className="h-3 w-3 inline mr-1" />
                  Location verified and geocoded for accurate mapping
                </div>
              )}
            </div>
          </motion.div>
        </Card>

        {/* Experience Categories */}
        <Card className="p-6 relative border-l-4 border-l-yellow-500">
          {renderEditButton('experience', 'Edit experience categories')}
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-yellow-600" />
              <h4 className="font-semibold text-gray-900">Experience Categories</h4>
              <UIBadge variant="outline" className="ml-2">{formData.businessData.experiences.length} Selected</UIBadge>
            </div>
            
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {formData.businessData.experiences.map((experience, index) => (
                  <div 
                    key={experience.id} 
                    className="bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-lg border border-yellow-200"
                  >
                    <h5 className="font-medium text-gray-900 text-sm">{experience.name}</h5>
                    <p className="text-xs text-gray-600 mt-1">{experience.description}</p>
                    <UIBadge className={`mt-2 text-xs ${
                      experience.type === 'animal' 
                        ? 'bg-orange-100 text-orange-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {experience.type === 'animal' ? '🦁 Animal' : '🏔️ Adventure'}
                    </UIBadge>
                  </div>
                ))}
              </div>
              
              {formData.businessData.additionalInfo && (
                <div className="mt-4 space-y-1">
                  <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    Additional Information
                  </p>
                  <div className="bg-gray-50 px-3 py-2 rounded-md">
                    <p className="text-gray-900 text-sm leading-relaxed">{formData.businessData.additionalInfo}</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </Card>
      </div>

      {/* Final Confirmation */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-green-900 mb-2">Ready to Submit?</h4>
            <p className="text-sm text-green-800 mb-3">
              By submitting this registration, you confirm that all information provided is accurate and agree to our terms of service. 
              You'll receive a verification email to activate your account.
            </p>
            <ul className="text-xs text-green-700 space-y-1">
              <li>• Your business profile will be reviewed within 24-48 hours</li>
              <li>• You'll receive email notifications about your registration status</li>
              <li>• You can edit your profile after verification</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={onBack}
          className="min-w-[120px] transition-all duration-200 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onSubmit}
          className="min-w-[140px] bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 transition-all duration-200 shadow-lg"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          Submit Registration
        </Button>
      </div>
    </motion.div>
  );
} 